import { createSelector } from '@reduxjs/toolkit';
import { RootState } from 'MyTypes';
import { keyBy } from 'lodash';

import { isLocationTypeGlobal } from '@portal/common/models/location-types';
import { LocationEntity } from '@portal/common/types';

import getLocationsHierarchyKey from '../../utility/get-location-hierarchy-key';

export const getEntityHierarchiesLoaded = createSelector(
    (state) => state.entityHierarchies,
    ({
        causes,
        cros,
        etiologies,
        impairments,
        injuries,
        locations,
        races,
        risks,
        sequelas,
        sevs,
        causeICDCodes,
        vehss,
        vaccinationStatuses,
    }) =>
        !!causes &&
        !!causeICDCodes &&
        !!cros &&
        !!etiologies &&
        !!impairments &&
        !!injuries &&
        !!locations &&
        !!races &&
        !!risks &&
        !!sequelas &&
        !!sevs &&
        !!vehss &&
        !!vaccinationStatuses
);

export const createGetEntityHierachyOf =
    <Key extends keyof RootState['entityHierarchies']>(key: Key) =>
    ({ entityHierarchies }: RootState): RootState['entityHierarchies'][Key] =>
        entityHierarchies[key];

export const createGetEntityHierarchyByIdOf = <Key extends keyof RootState['entityHierarchies']>(
    key: Key
) =>
    createSelector(
        createGetEntityHierachyOf(key),
        (
            entityHierarchy: RootState['entityHierarchies'][Key]
        ): Record<number, RootState['entityHierarchies'][Key]> | null => {
            if (entityHierarchy == null) {
                return null;
            }

            return keyBy(entityHierarchy, 'id');
        }
    );

export const getCauses = createGetEntityHierachyOf('causes');
export const getCausesById = createGetEntityHierarchyByIdOf('causes');

export const getSequelas = createGetEntityHierachyOf('sequelas');
export const getSequelasById = createGetEntityHierarchyByIdOf('sequelas');

export const getCros = createGetEntityHierachyOf('cros');
export const getCrosById = createGetEntityHierarchyByIdOf('cros');

export const getRisks = createGetEntityHierachyOf('risks');
export const getRisksById = createGetEntityHierarchyByIdOf('risks');

export const getEtiologies = createGetEntityHierachyOf('etiologies');
export const getEtiologiesById = createGetEntityHierarchyByIdOf('etiologies');

export const getImpairments = createGetEntityHierachyOf('impairments');
export const getImpairmentsById = createGetEntityHierarchyByIdOf('impairments');

export const getInjuries = createGetEntityHierachyOf('injuries');
export const getInjuriesById = createGetEntityHierarchyByIdOf('injuries');

export const getSevs = createGetEntityHierachyOf('sevs');
export const getSevsById = createGetEntityHierarchyByIdOf('sevs');

export const getCauseICDCodes = createGetEntityHierachyOf('causeICDCodes');
export const getCauseICDCodesById = createGetEntityHierarchyByIdOf('causeICDCodes');

export const getLocations = createGetEntityHierachyOf('locations');
export const getLocationsById = createGetEntityHierarchyByIdOf('locations');

export const getLocationsByDataset = createGetEntityHierachyOf('locationsByDataset');

export const getDatasetLocations = createSelector(
    getLocationsHierarchyKey,
    getLocationsByDataset,
    (key, locationsByDataset) => (key ? locationsByDataset?.[key] : null)
);

export const getDatasetLocationsById = createSelector(
    getLocations,
    (hierarchy): Record<number, LocationEntity[]> | null => {
        if (!hierarchy) {
            return null;
        }

        return keyBy(hierarchy, 'id') as Record<number, LocationEntity[]>;
    }
);

export const getLocationChildrenById = createSelector(
    getLocations,
    (locations): Record<number, LocationEntity[]> | null => {
        if (!locations) {
            return null;
        }

        return locations.reduce((locationChildrenById, location) => {
            // for each location that has children
            if (!isLocationTypeGlobal(location.location_type_id)) {
                // find all direct children locations
                const locationChildren = locations.filter((possibleChild) => {
                    const possibleChildParentId =
                        possibleChild[`level${possibleChild.level - 1}_parent_id`];
                    return possibleChildParentId === location.id;
                });
                locationChildrenById[location.id] = locationChildren.length
                    ? locationChildren
                    : null;
            }
            return locationChildrenById;
        }, {});
    }
);

export const getVaccinationStatuses = createGetEntityHierachyOf('vaccinationStatuses');
export const getVaccinationStatusesById = createGetEntityHierarchyByIdOf('vaccinationStatuses');

export const getRaces = createGetEntityHierachyOf('races');
export const getRacesById = createGetEntityHierarchyByIdOf('races');
